import {RequestContext} from "@/core/composable/createRequestContext";
import {useCommonStore} from "@/core/store/common";
import {useConfig} from "@/core/store/config";
import {TaskStatus} from "@/core/types";
import {paramToRef} from "@/core/vue/paramToRef";
import {type ClientExtended, createClientExtended} from "@/gql";
import {
    ErrorInterface,
    GenqlError,
    Message,
    Mutation,
    MutationGenqlSelection,
    Query,
    QueryGenqlSelection,
    Response,
    ResponseState
} from "@/gql/gen";
import debounce from "lodash-es/debounce";
import throttle from "lodash-es/throttle";
import {defineStore} from "pinia";
import {computed, Ref, ref, shallowRef, toValue, watch} from "vue";
import useErrorManager from "../error/useErrorManager";
import {getTypedEntries} from "../util/getTypedEntries";
import {createGraphqlFetcher} from "./fetcher";
import {
    GqlMutationGenerator,
    GqlQueryGenerator,
    GqlRequestFetchParams,
    GqlRequestMutationParams,
    GqlRequestQueryParams,
} from "./types";
import {HttpError} from "@/core/request";

export type UseQuery = {
    setEmptyResult: () => {},
    clearResult: () => {},
    invalidateCache: () => {},
    query: (variables?: any, params?: GqlRequestFetchParams) => void,
    loading: Ref<boolean>,
    loaded: Ref<boolean>,
    result: Ref<any>,
    onResult: (result: any) => void,
    status: Ref<TaskStatus>,
    isEmptyResult: Ref<boolean>
}

export type HeadersHook = (headers: HeadersInit) => HeadersInit

export const useGraphql = defineStore("graphql", () => {
    const {get} = useConfig();

    const {getGlobalAlertsChannel, getGlobalRequestContext} = useCommonStore();

    const requestHeaders = ref<Record<string, HeadersInit | HeadersHook>>({})

    const getRequestHeaders = () => {
        let result: HeadersInit = {}
        for (const [scope, headers] of getTypedEntries(requestHeaders.value)) {
            if (typeof headers === 'function') {
                headers(result)
            } else (typeof headers === 'object')
            {
                result = {
                    ...result,
                    ...headers
                }
            }
        }
        return result
    }

    function setRequestHeaders(scope: string, hook: HeadersInit | HeadersHook) {
        requestHeaders.value[scope] = hook
    }

    const client: ClientExtended = createClientExtended({
        url: get('app', 'API_GRAPHQL_URL'),
        fetcher: createGraphqlFetcher(get('app', 'API_GRAPHQL_URL')),
        headers: getRequestHeaders
    });

    const errorProcessor = (e: GenqlError | HttpError, requestContext?: RequestContext) => {
        const alertsChannel =
            requestContext?.getAlertsChannel() || getGlobalAlertsChannel();

        const messages: Partial<Message>[] = [];

        let status: string = "";

        console.error('errorProcessor', JSON.parse(JSON.stringify(e)));

        if (e instanceof GenqlError) {
            if (e.errors) {
                e.errors.forEach((error) => {
                    if (error.extensions?.status) {
                        status = error.extensions?.status;
                    }
                    if (e.alertShow) {
                        messages.push({
                            type: "error",
                            message: e.getAlertMessage()
                        });
                    }
                });
            }
        } else {
            if (e.alertShow) {
                messages.push({
                    type: "error",
                    message: e.getAlertMessage()
                });
            }
        }

        alertsChannel?.showMessages(messages);

        return {
            status,
        };
    };

    const processResponseState = (
        state: ResponseState,
        requestContext?: RequestContext
    ) => {
        const alertsChannel =
            requestContext?.getAlertsChannel() || getGlobalAlertsChannel();

        const messages = state.messages.filter((message) => message);

        console.log("processResponseState", {
            responseContext: requestContext,
            alertsChannel,
            messages,
        });

        alertsChannel?.showMessages(messages);
    };

    const responseSelection = (errorMultiple = true): any => {
        const selection: any = {
            success: true,
            error: {
                __fragment: "ErrorFields",
            },
            state: {
                __fragment: "ResponseState",
            },
        };

        if (errorMultiple) {
            selection.errors = {
                __fragment: "ErrorFields",
            };
        }
        return selection;
    };

    const query = async <T>(
        query: QueryGenqlSelection,
        requestParams: GqlRequestQueryParams<any, any> = {},
    ): Promise<T | null> => {
        let {isMultiple, timeout, retries, retryPolicy} = requestParams;

        // Передаем timeout, retries и retryPolicy напрямую в клиент
        const options = {
            timeout,
            retries,
            retryPolicy
        };

        let res = null;
        let data = await client.query(query, options);
        if (!isMultiple) {
            const returnField = Object.keys(data)[0] as keyof Query;
            res = data[returnField]
        } else {
            res = data;
        }
        return res as T
    }

    const queryWrapped = async <T>(
        _query: QueryGenqlSelection,
        params: GqlRequestQueryParams<any, any> = {},
        context: RequestContext = getGlobalRequestContext()
    ): Promise<T | null> => {
        const {throwOnError = false, errorProcessorDisable = false, processing} = params;
        if (processing)
            processing.value = true
        let res = null;
        try {
            res = await query<T>(_query, params);
        } catch (e: any) {
            if (!errorProcessorDisable) {
                errorProcessor(e, context);
            }
            if (throwOnError) {
                throw e;
            }
        } finally {
            if (processing)
                processing.value = false
        }
        return res as T
    };

    const mutation = async <T>(
        mutation: MutationGenqlSelection,
        requestParams: GqlRequestMutationParams<any, any> = {}
    ): Promise<T | null> => {
        let {isMultiple, timeout, retries, retryPolicy} = requestParams;

        // Передаем timeout, retries и retryPolicy напрямую в клиент
        const options = {
            timeout,
            retries,
            retryPolicy
        };

        let res = null;
        const data = await client.mutation(mutation, options);
        if (!isMultiple) {
            const field = Object.keys(data)[0] as keyof Mutation;
            res = data[field];
        } else {
            res = data;
        }
        return res as T
    };

    const mutationWrapped = async <T>(
        _mutation: MutationGenqlSelection,
        params: GqlRequestMutationParams<any, any> = {},
        context: RequestContext = getGlobalRequestContext()
    ): Promise<T> => {
        context.getAlertsChannel()?.onRequestStart();

        const {name, isMultiple, throwOnError = false, processing} = params

        let res = null;

        if (processing)
            processing.value = true

        try {
            if (!isMultiple) {
                res = (await mutation<T>(_mutation, params)) as Response | null;
                if (res) {
                    if (res.state) {
                        processResponseState(res.state, context);
                    }
                }
            } else {
                res = (await mutation<T>(_mutation)) as Record<string, Response> | null;
                if (res) {
                    for (const [name, result] of getTypedEntries(res)) {
                        if (result.state) {
                            processResponseState(result.state, context);
                        }
                    }
                }
            }
        } catch (e: any) {
            console.error(e);
            const {status} = errorProcessor(e, context);
            if (throwOnError) {
                throw e;
            }
            res = {
                status,
                success: false,
            };
        } finally {
            if (processing)
                processing.value = false
        }
        return res as T;
    }

    const useMutation = <
        T extends GqlMutationGenerator<Omit<Response, "__typename">, any>
    >(
        generator: T,
        params: GqlRequestMutationParams<T['_variables'], T['_result']> = {},
        context: RequestContext = getGlobalRequestContext()
    ) => {
        const {name, throwOnError = false, isMultiple, errorManager: errorManagerParams} = params;
        const loading = ref<boolean>(false);
        const loaded = ref<boolean>(false);
        const startTimestamp = ref<number>(0);
        const finishTimestamp = ref<number>(0);

        const result = ref<T['_result']>();

        const error = computed(() => {
            if (result?.value?.error) {
                return result?.value?.error;
            } else {
                return result?.value?.errors[0];
            }
        });

        const errors = computed(() => {
            if (result?.value?.errors) {
                return result?.value?.errors;
            } else if (result?.value?.error) {
                return [result?.value?.error];
            } else {
                return [];
            }
        });

        const errorType = computed(() => error.value?.type);

        const errorName = computed(() => error.value?.name);

        const success = computed(() => result?.value?.success);

        const _params = {
            ...params,
            isMultiple: false,
        };

        const errorManager = useErrorManager([], errorManagerParams)

        const processResult = (res: Response) => {
            let _errors: ErrorInterface[] = []
            if (res.errors && res.errors.length) {
                _errors = res.errors
            } else if (res.error) {
                _errors = [res.error]
            }
            errorManager.setErrors(_errors)
        }

        type OnResultCallback = (result: T["_result"]) => void;

        const callbacks: {
            onResult: OnResultCallback[];
        } = {
            onResult: [],
        };

        const onResult = (callback: (result: T["_result"]) => void) => {
            callbacks.onResult.push(callback);
        };

        const runCallbacks = (name: keyof typeof callbacks, ...args: any[]) => {
            switch (name) {
                case "onResult":
                    if (_params.onResult)
                        _params.onResult(args[0])
                    break;
            }
            callbacks[name].forEach((callback) => {
                callback.apply(null, args as any);
            });
        };

        const variablesRef = paramToRef<T["_variables"]>(params.variables);

        const mutate: T["mutate"] = async (variables = null, fetchParams: GqlRequestFetchParams = {}) => {


            const _variables = variables || toValue(variablesRef) || {}

            let res: Response;

            startTimestamp.value = Date.now();

            const query = generator.build(_variables);

            loading.value = true;

            try {
                // Используем таймаут, retries и retryPolicy из fetchParams или из params
                const timeout = fetchParams.timeout ?? params.timeout;
                const retries = fetchParams.retries ?? params.retries;
                const retryPolicy = fetchParams.retryPolicy ?? params.retryPolicy;
                const mutationParams = {
                    ..._params,
                    ...(timeout !== undefined && {timeout}),
                    ...(retries !== undefined && {retries}),
                    ...(retryPolicy && {retryPolicy})
                };

                res = await mutationWrapped(query, mutationParams, context);
                loaded.value = true;
                result.value = res as T['_result'];

                runCallbacks('onResult', res)
            } catch (e) {
                throw e;
            } finally {
                loading.value = false;
                finishTimestamp.value = Date.now();
            }
            processResult(res)
            return res;
        };

        return {
            name,
            startTimestamp,
            finishTimestamp,
            mutate,
            onResult,
            loading,
            loaded,
            result,

            success,
            errorManager,
        };
    };

    const useQuery = <T extends GqlQueryGenerator<any, any>>(
        generator: T,
        params: GqlRequestQueryParams<T['_variables'], T['_result']> = {},
        context: RequestContext = getGlobalRequestContext()
    ) => {

        const _params = {
            enabled: undefined as Ref<boolean> | undefined,
            throwOnError: true,
            ...params,
            isMultiple: false,
        };

        const {
            initialResult,
            debounced,
            throttled,
            checkEmptyResult
        } = _params

        const getInitialResultValue = () => {
            if (initialResult) {
                const _initialResult = initialResult()
                const value = toValue(_initialResult)
                if (typeof value !== 'undefined' && value !== null) {
                    return value
                }
            }
            return null
        }

        const initial = getInitialResultValue()

        const status = ref<TaskStatus>(initial === null ? 'idle' : 'resolved');
        const loading = ref(false);
        const loaded = ref(false);
        const startedAt = ref<number>(0);
        const finishedAt = ref<number>(0);
        const resolvedAt = ref<number>(0);
        const runCount = ref<number>(0);

        const variablesRef = paramToRef<T["_variables"]>(params.variables);

        const result = shallowRef<T["_result"]>();

        const isEmptyResult = computed(() => {
            if (result.value) {
                if (checkEmptyResult) {
                    return checkEmptyResult(result.value)
                } else {
                    if (Array.isArray(result.value)) {
                        return !result.value.length
                    } else {
                        return !result.value
                    }
                }
            } else {
                return true
            }
        })

        const forceDisabled = ref(params.lazy)
        const enabledOption = computed(() => params.enabled?.value === undefined || params.enabled?.value)
        const isEnabled = computed(() => enabledOption.value && !forceDisabled.value)

        type OnResultCallback = (result: T["_result"]) => void;

        const callbacks: {
            onResult: OnResultCallback[];
        } = {
            onResult: [],
        };

        const onResult = (callback: (result: T["_result"]) => void) => {
            callbacks.onResult.push(callback);
        };

        const runCallbacks = (name: keyof typeof callbacks, ...args: any[]) => {
            switch (name) {
                case "onResult":
                    if (_params.onResult)
                        _params.onResult(args[0])
                    break;
            }
            callbacks[name].forEach((callback) => {
                callback.apply(null, args as any);
            });
        };

        const setEmptyResult = () => {
            if (result.value) {
                if (Array.isArray(result.value)) {
                    result.value = []
                } else {
                    result.value = null
                }
            }
        }

        const clearResult = () => {
            result.value = undefined
        }

        const setResult = (res: T["_result"]) => {
            result.value = res
        }

        const invalidateCache = () => {
            resolvedAt.value = 0
        }

        const queryInternal: T["run"] = async (variables, fetchParams: GqlRequestFetchParams = {}) => {

            runCount.value++

            if (!fetchParams.refetch) {
                if (runCount.value === 1) {
                    const initial = getInitialResultValue()
                    if (initial !== null) {
                        status.value = 'resolved'
                        result.value = initial
                        resolvedAt.value = Date.now()
                    }
                }

                if (_params.cache && resolvedAt.value && result.value) {
                    if (typeof _params.cache === "number") {
                        const age = Date.now() - resolvedAt.value
                        if (age < _params.cache * 1000) {
                            runCallbacks("onResult", result.value);
                            return result.value
                        }
                    }
                }
            }

            let res: any;

            const query = generator.build(variables);

            startedAt.value = Date.now();
            loading.value = true;
            status.value = 'processing'

            try {
                // Используем таймаут, retries и retryPolicy из fetchParams или из params
                const timeout = fetchParams.timeout ?? _params.timeout;
                const retries = fetchParams.retries ?? _params.retries;
                const retryPolicy = fetchParams.retryPolicy ?? _params.retryPolicy;
                const queryParams = {
                    ..._params,
                    ...(timeout !== undefined && {timeout}),
                    ...(retries !== undefined && {retries}),
                    ...(retryPolicy && {retryPolicy})
                };

                res = await queryWrapped(query, queryParams, context);
                loaded.value = true;
                result.value = res as T['_result'];
                runCallbacks("onResult", res);
                resolvedAt.value = Date.now()
                status.value = 'resolved'
            } catch (e) {
                status.value = 'rejected'
                console.log('reject', e)
            } finally {
                loading.value = false;
                finishedAt.value = Date.now();
            }
            return res;
        };

        let queryProxy: any;

        if (debounced) {
            queryProxy = debounce(queryInternal, debounced);
        }

        if (throttled) {
            queryProxy = throttle(queryInternal, throttled);
        }
        const query = async (variables: T["_variables"]  = null, fetchParams: GqlRequestFetchParams = {}) => {
            const _variables = variables || toValue(variablesRef) || {}
            return !fetchParams.immediate && queryProxy
                ? queryProxy(_variables, fetchParams)
                : queryInternal(_variables, fetchParams);
        };

        if (isEnabled.value) {
            query(null)
        }

        let pollingInterval: any = null

        const startPolling = () => {
            pollingInterval = setInterval(() => {
                query(null, {refetch: true})
            }, params.pollingInterval)
        }

        const stopPolling = () => {
            clearInterval(pollingInterval)
        }

        if (params.pollingInterval) {
            startPolling()
        }

        watch(variablesRef, () => {
            console.log('watch variables')
            if (isEnabled.value) {
                query(null)
            }
        })

        return {
            startTimestamp: startedAt,
            finishTimestamp: finishedAt,
            query,
            loading,
            loaded,
            result,
            onResult,
            status,
            isEmptyResult,
            setResult,
            clearResult,
            setEmptyResult,
            invalidateCache,
            startPolling,
            stopPolling,
            isEnabled
        };
    };

    const useLazyQuery = <T extends GqlQueryGenerator<any, any>>(
        generator: T,
        params: GqlRequestQueryParams<T['_variables'], T['_result']> = {},
        context: RequestContext = getGlobalRequestContext()
    ) => {
        return useQuery(generator, {
            ...params,
            lazy: true,
        });
    };

    return {
        client,
        query,
        queryWrapped,
        mutation,
        mutationWrapped,
        useMutation,
        useQuery,
        useLazyQuery,
        responseSelection,
        setRequestHeaders
    };
});
